import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { hasError, isPersistable, isUpdating } from '@ublend-npm/aula-editor';
import { trimEnd } from '@ublend-npm/aula-editor-utils';

const withFileBlockValidation = (AulaEditor) => {
  const FileBlockValidation = ({
    onChange,
    onReturn,
    forwardedRef,
    setSubmittingState,
    isSubmitting,
    conversationId,
    config,
    editorState,
    ...props
  }) => {
    useEffect(() => {
      if (!isSubmitting || isUpdating(editorState, config)) {
        return;
      }

      if (
        onReturn &&
        isPersistable(editorState, config) &&
        !hasError(editorState, config)
      ) {
        onReturn(trimEnd(editorState), conversationId);
      }

      if (setSubmittingState) {
        setSubmittingState(false);
      }
    }, [
      isSubmitting,
      editorState,
      config,
      onReturn,
      conversationId,
      setSubmittingState,
    ]);

    const handleOnReturn = useCallback(
      (nextEditorState) => {
        if (isUpdating(nextEditorState, config)) {
          if (setSubmittingState) {
            setSubmittingState(true);
          }
        } else if (
          isPersistable(nextEditorState, config) &&
          !hasError(nextEditorState, config)
        ) {
          onReturn(trimEnd(nextEditorState), conversationId);
        }
      },
      [config, conversationId, onReturn, setSubmittingState]
    );

    const handleOnChange = useCallback(
      (nextEditorState) => {
        if (onChange) {
          onChange(nextEditorState, conversationId);
        }

        // Deliver the message when the state is updated by the aula editor
        // and the submit is in progress until the state becomes persistable
        if (isSubmitting) {
          if (hasError(nextEditorState, config)) {
            if (setSubmittingState) {
              setSubmittingState(false);
            }
          } else if (!isUpdating(nextEditorState, config)) {
            if (onReturn && isPersistable(nextEditorState, config)) {
              onReturn(nextEditorState, conversationId);
            }
            if (setSubmittingState) {
              setSubmittingState(false);
            }
          }
        }
      },
      [
        isSubmitting,
        config,
        conversationId,
        onChange,
        onReturn,
        setSubmittingState,
      ]
    );

    return (
      <AulaEditor
        onChange={handleOnChange}
        onReturn={onReturn ? handleOnReturn : undefined}
        readOnly={isSubmitting}
        ref={forwardedRef}
        config={config}
        editorState={editorState}
        {...props}
      />
    );
  };

  FileBlockValidation.propTypes = {
    onChange: PropTypes.func.isRequired,
    onReturn: PropTypes.func,
    onFilesStatusChange: PropTypes.func,
    setSubmittingState: PropTypes.func,
    isSubmitting: PropTypes.bool,
    conversationId: PropTypes.string,
    config: PropTypes.shape({}),
    editorState: PropTypes.shape({}),
    forwardedRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
  };

  FileBlockValidation.defaultProps = {
    config: undefined,
    editorState: undefined,
    conversationId: undefined,
    onReturn: undefined,
    setSubmittingState: undefined,
    isSubmitting: false,
    onFilesStatusChange: undefined,
  };

  return React.forwardRef((props, ref) => {
    return <FileBlockValidation {...props} forwardedRef={ref} />;
  });
};

export default withFileBlockValidation;
